import {
  Autocomplete,
  Button,
  Dialog,
  DialogTitle,
  FormControlLabel,
  InputAdornment,
  Paper,
  Radio,
  TextField,
  Typography,
  RadioGroup,
} from "@mui/material";
import { Box, Stack } from "@mui/system";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import React, { useEffect, useRef, useState } from "react";
import Grid from "@mui/material/Grid2";
import AddIcon from "@mui/icons-material/Add";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import PercentIcon from "@mui/icons-material/Percent";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import IconButton from "@mui/material/IconButton";
import { PageGroupEndPointLogic } from "../../../../../logic/AssetManagement/PageGroups/pageGroupEndPointLogic";
import { useSelector } from "react-redux";
import { selectUserToken } from "../../../../../features/userSlice";
import { MetaEntitiesEndPointLogic } from "../../../../../logic/Admin/MetaEntities/MetaEntitiesEndPointLogic";
import { trackPromise } from "react-promise-tracker";
import { v4 as uuidv4 } from "uuid";
import CircularProgress from "@mui/material/CircularProgress";
import ClearIcon from "@mui/icons-material/Clear";
import { getEnvWidgetSourceUid } from "./pageGroupHelpers";
import GenericEndPointsLogic from "../../../../../logic/GenericEndPointsLogic/GenericEndPointsLogic";

// Static entity type sent to the variants endpoint for this module
const VARIANTS_ENTITY_TYPE = "page_group";
interface CreateNewRequestModalProps {
  open: boolean;
  handleClose: () => void;
  selectedSiteUid: string;
  setTableRefresh: any;
}

function CreateNewRequestModal({
  open,
  handleClose,
  setTableRefresh,
}: CreateNewRequestModalProps) {
  const [groupName, setGroupName] = useState("");
  const [themeProportion, setThemeProportion] = useState(100);
  const [popupProportion, setPopupProportion] = useState(100);
  const [showSecondTheme, setShowSecondTheme] = useState(false);
  const [showSecondPopup, setShowSecondPopup] = useState(false);
  const [selectedTheme2, setSelectedTheme2] = useState(null) as any;
  const [selectedPopup2, setSelectedPopup2] = useState(null) as any;
  const [, setIsDuplicateButtonDisabled] = useState(true);
  const [currentPage, setCurrentPage] = useState(1); // Track the current page
  const token = useSelector(selectUserToken);
  const [themeList, setThemeList] = useState([]);
  const [popUpList, setPopUpList] = useState([]);
  const [trafficSources, setTrafficSources] = useState([]);
  const [, setPopupShareUid] = useState("");
  const [, setThemeShareUid] = useState("");

  const [comparisonTableShareUid, setComparisonTableShareUid] = useState(
    "",
  ) as any;
  const [trafficSourceShareUid, setTrafficSourceShareUid] = useState("") as any;
  const [autocompleteList, setAutocompleteList] = useState([{ id: uuidv4() }]);
  const [autocompleteListCompTable, setAutocompleteListCompTable] = useState([
    { id: uuidv4() },
  ]);
  const [selectedTheme, setSelectedTheme] = useState(null) as any;
  const [selectedPopup, setSelectedPopup] = useState(null) as any;
  const [, setWebsiteAttributeDetails] = useState([]) as any;
  const [uploadingDevice, setUploadingDevice] = useState("");
  const [imagePath, setImagePath] = useState("");
  const [loading, setLoading] = useState(false);
  const [pageGroupData, setPageGroupData] = useState([]) as any;
  const [pageGroupCreationData, setPageGroupCreationData] = useState([]) as any;
  const [creationLoading, setCreationLoading] = useState(false);
  const { selectedSharedUid } = useSelector((state: any) => state.pageGroup);
  const [showGroupNameError, setShowGroupNameError] = useState<
    false | "length" | "spaces"
  >(false);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  // Handle input change and validate the minimum 8 character condition
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const sanitizedValue = value.replace(/[^a-zA-Z0-9_ ]/g, ""); // allow letters, numbers, underscores, spaces only
    if (sanitizedValue.startsWith(" ")) return;

    setGroupName(sanitizedValue);
    setIsDuplicateButtonDisabled(
      sanitizedValue.endsWith(" ") || sanitizedValue.length < 8,
    );

    // In handleInputChange, update the debounce callback:
    debounceRef.current = setTimeout(() => {
      if (sanitizedValue.endsWith(" ")) {
        setShowGroupNameError("spaces");
      } else if (sanitizedValue.length < 8) {
        setShowGroupNameError("length");
      } else {
        setShowGroupNameError(false);
      }
    }, 1000);
  };

  const getFilteredThemeOptions = (excludeTheme: any) => {
    return themeList.filter(
      (theme: any) => theme.shared_uid !== excludeTheme?.shared_uid,
    );
  };

  const getFilteredPopupOptions = (excludePopup: any) => {
    return popUpList
      ?.filter((popup: any) => popup.type === "Popup Offer" && popup?.active)
      .filter((popup: any) => popup.shared_uid !== excludePopup?.shared_uid);
  };

  const handleThemeProportionChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = parseInt(e.target.value, 10);
    const clampedValue = Math.min(Math.max(value || 0, 0), 100);
    setThemeProportion(clampedValue);
    setShowSecondTheme(clampedValue < 100);
    if (clampedValue === 100) setSelectedTheme2(null);
  };

  const handlePopupProportionChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = parseInt(e.target.value, 10);
    const clampedValue = Math.min(Math.max(value || 0, 0), 100);
    setPopupProportion(clampedValue);
    setShowSecondPopup(clampedValue < 100);
    if (clampedValue === 100) setSelectedPopup2(null);
  };

  const handleAddTrafficSource = () => {
    setAutocompleteList((prevList) => [...prevList, { id: uuidv4() }]);
  };

  const handleAddCompTable = () => {
    setAutocompleteListCompTable((prevList) => [...prevList, { id: uuidv4() }]);
  };

  // Handle the "Next" button click
  const handleNext = () => {
    setCurrentPage(currentPage + 1); // Move to the next page
  };

  // Handle the "Back" button click
  const handleBack = () => {
    setCurrentPage(currentPage - 1); // Move to the previous page
  };

  useEffect(() => {
    if (open === true) {
      // static shared uids
      const popupSharedUid = getEnvWidgetSourceUid(selectedSharedUid);
      Promise.all([
        PageGroupEndPointLogic.getAllThemes(
          token,
          selectedSharedUid,
          setThemeList,
        ),
        PageGroupEndPointLogic.getAllPopups(
          token,
          popupSharedUid,
          setPopUpList,
        ),
        MetaEntitiesEndPointLogic.getMetaTrafficSource(
          token,
          setTrafficSources,
          trackPromise,
        ),
        PageGroupEndPointLogic.getWebsiteAttributeDetails(
          token,
          selectedSharedUid,
          setWebsiteAttributeDetails,
        ),
      ]);
    }
    // eslint-disable-next-line
  }, [open]);

  // const [isLoading,setIsLoading] = useState(false);

  const handleCancel = () => {
    handleClose();
    setGroupName("");
    // setThemeProportion("");
    // setPopupProportion("");
    setSelectedTheme(null);
    setSelectedPopup(null);
    setSelectedTheme2(null);
    setSelectedPopup2(null);
    setShowSecondTheme(false);
    setShowSecondPopup(false);
    setThemeProportion(100);
    setPopupProportion(100);
    setAutocompleteList([{ id: uuidv4() }]);
    setAutocompleteListCompTable([{ id: uuidv4() }]);
    setCurrentPage(1);
    setComparisonTableShareUid("");
    setTrafficSourceShareUid("");
    setPageGroupData([]);
    setCurrentPage(1);
    setHeadlineCustomValue({
      desktop: "",
      mobile: "",
      tablet: "",
    });
    setBase64Images({
      desktop: null,
      mobile: null,
      tablet: null,
    });
    setBannerImagePathDesktop("");
    setBannerImagePathMobile("");
    setBannerImagePathTablet("");
    setbannerState({
      desktop: "inherit",
      mobile: "inherit",
      tablet: "inherit",
    });
    setHeadlineState({
      desktop: "inherit",
      mobile: "inherit",
      tablet: "inherit",
    });
    setShowGroupNameError(false);
    setImagePath("");
    setUploadingDevice("");
    setPageGroupCreationData(false);
    setTableRefresh(false);
    setCreationLoading(false);
    setLoading(false);
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
  };

  const [headlineState, setHeadlineState] = useState({
    desktop: "inherit",
    mobile: "inherit",
    tablet: "inherit",
  }) as any;

  const [bannerState, setbannerState] = useState({
    desktop: "inherit",
    mobile: "inherit",
    tablet: "inherit",
  }) as any;

  const [headlineCustomValue, setHeadlineCustomValue] = useState({
    desktop: "",
    mobile: "",
    tablet: "",
  }) as any;

  const [base64Images, setBase64Images] = useState<{
    [key: string]: string | null;
  }>({
    desktop: null,
    mobile: null,
    tablet: null,
  }) as any;

  const [bannerImagePathDesktop, setBannerImagePathDesktop] = useState("");
  const [bannerImagePathMobile, setBannerImagePathMobile] = useState("");
  const [bannerImagePathTablet, setBannerImagePathTablet] = useState("");

  const handleHeadlineChange = (device: string, value: string) => {
    setHeadlineState((prevState: any) => ({
      ...prevState,
      [device]: value,
    }));
  };

  const handleBannerChange = (device: string, value: string) => {
    setbannerState((prevState: any) => ({
      ...prevState,
      [device]: value,
    }));
  };

  const handleTextChange = (device: string, value: any) => {
    setHeadlineCustomValue((prevState: any) => ({
      ...prevState,
      [device]: value,
    }));
  };

  const handleFileUpload = (
    device: string,
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    if (file && file.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setBase64Images((prevState: any) => ({
          ...prevState,
          [device]: {
            base64: reader.result as string, // Store the base64 image
            fileName: file.name, // Store the file name
          },
        }));
      };
      reader.readAsDataURL(file);
    } else {
      alert("Please upload a valid image file.");
    }
  };

  const imageUploadData = {
    data: {
      base64_image:
        uploadingDevice === "desktop"
          ? base64Images.desktop?.base64
          : uploadingDevice === "mobile"
            ? base64Images.mobile?.base64
            : base64Images.tablet?.base64,
      file_name:
        uploadingDevice === "desktop"
          ? base64Images.desktop?.fileName
          : uploadingDevice === "mobile"
            ? base64Images.mobile?.fileName
            : base64Images.tablet?.fileName,
    },
  };

  const data = {
    group_name: groupName,
    headlines: {
      mobile:
        headlineState?.mobile === "custom" ? headlineCustomValue?.mobile : null,
      tablet:
        headlineState?.tablet === "custom" ? headlineCustomValue?.tablet : null,
      desktop:
        headlineState?.desktop === "custom"
          ? headlineCustomValue?.desktop
          : null,
    },
    banners: {
      mobile: bannerState?.mobile === "inherit" ? null : bannerImagePathMobile,
      tablet: bannerState?.tablet === "inherit" ? null : bannerImagePathTablet,
      desktop:
        bannerState?.desktop === "inherit" ? null : bannerImagePathDesktop,
    },
    ...(showSecondTheme && selectedTheme2
      ? { theme_v1_proportions: String(themeProportion) }
      : {}),
    themes: [
      ...(selectedTheme?.shared_uid ? [selectedTheme.shared_uid] : []),
      ...(showSecondTheme && selectedTheme2?.shared_uid
        ? [selectedTheme2.shared_uid]
        : []),
    ],
    ...(showSecondPopup && selectedPopup2
      ? { popup_v1_proportions: popupProportion }
      : {}),
    popups: [
      ...(selectedPopup?.shared_uid ? [selectedPopup.shared_uid] : []),
      ...(showSecondPopup && selectedPopup2?.shared_uid
        ? [selectedPopup2.shared_uid]
        : []),
    ],
    comparison_tables: comparisonTableShareUid,
    traffic_sources: trafficSourceShareUid,
  };

  useEffect(() => {
    const uploadImage = async () => {
      if (uploadingDevice && base64Images[uploadingDevice]?.base64) {
        try {
          setLoading(true);
          await PageGroupEndPointLogic.postImageUpload(
            token,
            selectedSharedUid,
            setImagePath,
            imageUploadData,
          );
        } catch (error) {
          console.error("Image upload failed:", error);
        } finally {
          setLoading(false);
          setBase64Images({
            desktop: null,
            mobile: null,
            tablet: null,
          });
        }
      }
    };
    uploadImage();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [base64Images, uploadingDevice]);

  useEffect(() => {
    const createPageGroup = async () => {
      if (data && currentPage === 2 && pageGroupCreationData) {
        try {
          setCreationLoading(true);
          const createResponse = await PageGroupEndPointLogic.createPageGroup(
            token,
            selectedSharedUid,
            setPageGroupData,
            data,
            "Created",
          );
          // Create response returns the new page group's shared_uid directly in `data`
          const pageGroupSharedUid = createResponse?.data?.data;
          if (typeof pageGroupSharedUid === "string" && pageGroupSharedUid) {
            GenericEndPointsLogic.updateVariants({
              oauthTokenHeader: token,
              websiteSharedUid: selectedSharedUid,
              entityType: VARIANTS_ENTITY_TYPE,
              entitySharedUid: pageGroupSharedUid,
            });
          }
        } catch (error) {
          console.error("Error creating page group:", error);
        } finally {
          setPageGroupCreationData(false);
          setCreationLoading(false);
        }
      }
    };
    createPageGroup();
    // eslint-disable-next-line
  }, [pageGroupCreationData === true]);

  if (Object.values(pageGroupData).length > 0) {
    handleCancel();
    setTableRefresh(true);
  }

  useEffect(() => {
    if (uploadingDevice === "desktop") {
      setBannerImagePathDesktop(imagePath);
    } else if (uploadingDevice === "mobile") {
      setBannerImagePathMobile(imagePath);
    } else {
      setBannerImagePathTablet(imagePath);
    }
    // eslint-disable-next-line
  }, [imagePath]);

  return (
    <>
      {loading && (
        <Dialog open={open} onClose={handleClose}>
          <Paper sx={{ padding: "16px 24px 20px 24px" }}>
            <CircularProgress />
          </Paper>
        </Dialog>
      )}
      <Dialog
        open={open}
        onClose={(_, reason) => {
          if (reason !== "backdropClick") {
            handleClose();
          }
        }}
        // PaperProps={{ sx: { width: "900px", maxWidth: "900px" } }}
        fullWidth
        maxWidth="md"
      >
        <Paper sx={{ padding: "16px 24px 20px 24px" }}>
          <DialogTitle sx={{ padding: "0px 0px 4px 0px" }}>
            Create a New Page Group
          </DialogTitle>
          {currentPage === 1 && (
            <>
              <Box>
                <Grid container>
                  <Grid size={12}>
                    <TextField
                      id="outlined-multiline-flexible"
                      label="Group Name" // Set your label here
                      placeholder="How you like the site to be called" // Set your placeholder here
                      required
                      fullWidth
                      color="secondary"
                      size="small"
                      margin="dense"
                      value={groupName}
                      error={showGroupNameError !== false}
                      helperText={
                        showGroupNameError === "spaces"
                          ? "Group name cannot start or end with spaces"
                          : showGroupNameError === "length"
                            ? "Group name must be at least 8 characters long"
                            : ""
                      }
                      onChange={handleInputChange} // Update state on change
                    />
                    <Typography
                      sx={{
                        fontSize: "12px",
                        marginTop: "8px",
                        color: "#00000099",
                      }}
                    >
                      minimum of 8 characters, only letters and numbers.
                    </Typography>
                  </Grid>
                </Grid>
                <Box mt={2} sx={{ flexGrow: 1 }}>
                  <Typography>Themes</Typography>
                  {/* First theme row */}
                  <Grid container mt={0.5}>
                    <Grid size={6}>
                      <Autocomplete
                        sx={{ width: 418, mb: showSecondTheme ? 1 : 0 }}
                        value={selectedTheme}
                        disablePortal
                        size="small"
                        options={getFilteredThemeOptions(selectedTheme2)}
                        getOptionLabel={(theme: any) => theme.name}
                        onChange={(_, theme: any) => {
                          setThemeShareUid(theme?.shared_uid || null);
                          setSelectedTheme(theme || null);
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Theme"
                            required
                            color="secondary"
                            placeholder="Choose Page Structure Theme from the list"
                          />
                        )}
                      />
                    </Grid>
                    <Grid size={3} sx={{ pl: 1 }}>
                      <TextField
                        id="page-structure-proportion"
                        label="Proportion"
                        required
                        color="secondary"
                        size="small"
                        fullWidth
                        type="number"
                        value={themeProportion}
                        onChange={handleThemeProportionChange}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <PercentIcon />
                            </InputAdornment>
                          ),
                          inputProps: { min: 0, max: 100 },
                        }}
                      />
                    </Grid>
                  </Grid>
                  {/* Second theme row - shown when proportion < 100 */}
                  {showSecondTheme && (
                    <Grid container mt={0.5}>
                      <Grid size={6}>
                        <Autocomplete
                          sx={{ width: 418 }}
                          value={selectedTheme2}
                          disablePortal
                          size="small"
                          options={getFilteredThemeOptions(selectedTheme)}
                          getOptionLabel={(theme: any) => theme.name}
                          onChange={(_, theme: any) => {
                            setSelectedTheme2(theme || null);
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="Theme"
                              required
                              color="secondary"
                              placeholder="Choose Page Structure Theme from the list"
                            />
                          )}
                        />
                      </Grid>
                      <Grid size={3} sx={{ pl: 1 }}>
                        <TextField
                          id="page-structure-proportion-2"
                          label="Proportion"
                          required
                          color="secondary"
                          size="small"
                          fullWidth
                          value={100 - themeProportion}
                          disabled
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <PercentIcon />
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>
                    </Grid>
                  )}
                </Box>
                <Box mt={2} sx={{ flexGrow: 1 }}>
                  <Typography>Popups</Typography>
                  {/* First popup row */}
                  <Grid container mt={0.5}>
                    <Grid size={6}>
                      <Autocomplete
                        sx={{ width: 418, mb: showSecondPopup ? 1 : 0 }}
                        disablePortal
                        size="small"
                        options={getFilteredPopupOptions(selectedPopup2)}
                        value={selectedPopup}
                        getOptionLabel={(popup: any) => popup.widget_name}
                        onChange={(_, popup) => {
                          setPopupShareUid(popup?.shared_uid);
                          setSelectedPopup(popup || null);
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="popup"
                            required
                            color="secondary"
                            placeholder="Choose Popup Content from the list"
                          />
                        )}
                      />
                    </Grid>
                    <Grid size={3} sx={{ pl: 1 }}>
                      <TextField
                        id="popup-proportion"
                        label="Proportion"
                        required
                        color="secondary"
                        size="small"
                        fullWidth
                        type="number"
                        value={popupProportion}
                        onChange={handlePopupProportionChange}
                        InputProps={{
                          endAdornment: (
                            <InputAdornment position="end">
                              <PercentIcon />
                            </InputAdornment>
                          ),
                          inputProps: { min: 0, max: 100 },
                        }}
                      />
                    </Grid>
                  </Grid>
                  {/* Second popup row - shown when proportion < 100 */}
                  {showSecondPopup && (
                    <Grid container mt={0.5}>
                      <Grid size={6}>
                        <Autocomplete
                          sx={{ width: 418 }}
                          disablePortal
                          size="small"
                          options={getFilteredPopupOptions(selectedPopup)}
                          value={selectedPopup2}
                          getOptionLabel={(popup: any) =>
                            popup?.widget_name || popup?.name || ""
                          }
                          onChange={(_, popup) => {
                            setSelectedPopup2(popup || null);
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label="popup"
                              required
                              color="secondary"
                              placeholder="Choose Popup Content from the list"
                            />
                          )}
                        />
                      </Grid>
                      <Grid size={3} sx={{ pl: 1 }}>
                        <TextField
                          id="popup-proportion-2"
                          label="Proportion"
                          required
                          color="secondary"
                          size="small"
                          fullWidth
                          value={100 - popupProportion}
                          disabled
                          InputProps={{
                            endAdornment: (
                              <InputAdornment position="end">
                                <PercentIcon />
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>
                    </Grid>
                  )}
                </Box>
                <Box mt={2}>
                  <Typography>Assign Comparison Tables</Typography>
                  <Stack spacing={2} mt={0.5}>
                    {(autocompleteListCompTable.length > 0
                      ? autocompleteListCompTable
                      : [{}]
                    ) // Render a default empty object if the array is empty
                      .map((item: any, index: number) => (
                        <Stack
                          key={item.id || index}
                          flexDirection="row"
                          gap={2}
                        >
                          <Autocomplete
                            disablePortal
                            size="small"
                            options={popUpList.filter(
                              (popup: any) => popup.type === "Comparison Table",
                            )}
                            value={item.selectedValue || null} // Ensure null when no value is selected
                            getOptionLabel={(popup: any) => popup.widget_name}
                            onChange={(_, popup) => {
                              const updatedList = [
                                ...autocompleteListCompTable,
                              ];
                              const updatedItem = {
                                ...item,
                                selectedValue: popup || null,
                                shared_uid: popup?.shared_uid || null,
                              };
                              updatedList[index] = updatedItem; // Update the specific item
                              setAutocompleteListCompTable(updatedList); // Update the entire list

                              // Collect all shared_uids from the updated list
                              const allSharedUids = updatedList
                                .map((entry: any) => entry.shared_uid)
                                .filter((uid) => uid); // Filter out null or undefined values
                              setComparisonTableShareUid(allSharedUids); // Save the aggregated shared_uids
                            }}
                            sx={{ width: 418 }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Comparison Table"
                                color="secondary"
                                placeholder="Choose Comparison Table from the list"
                              />
                            )}
                          />
                          {index !== 0 && (
                            // Show "Remove" button for all other items
                            <Stack
                              alignItems="center"
                              flexDirection="row"
                              gap={1}
                            >
                              <ClearIcon
                                color="secondary"
                                sx={{
                                  cursor: "pointer",
                                  marginRight: "6px 16px",
                                }}
                                onClick={() => {
                                  const updatedList =
                                    autocompleteListCompTable.filter(
                                      (_, idx) => idx !== index,
                                    );
                                  setAutocompleteListCompTable(updatedList); // Update the list

                                  // Collect updated shared_uids after removal
                                  const allSharedUids = updatedList
                                    .map((entry: any) => entry.shared_uid)
                                    .filter((uid) => uid); // Filter out null or undefined values
                                  setComparisonTableShareUid(allSharedUids); // Save the aggregated shared_uids
                                }}
                              />
                            </Stack>
                          )}
                          {index === autocompleteListCompTable.length - 1 && (
                            // Add "Add Another" button to the last item only if it's not the first one
                            <Button
                              variant="outlined"
                              color="secondary"
                              onClick={handleAddCompTable}
                              sx={{ padding: "6px 16px", width: "192px" }}
                            >
                              <Stack
                                alignItems="center"
                                flexDirection="row"
                                gap={1}
                              >
                                Add another <AddIcon fontSize="small" />
                              </Stack>
                            </Button>
                          )}
                        </Stack>
                      ))}
                  </Stack>
                </Box>
                <Box mt={2}>
                  <Typography>Assign Traffic Source</Typography>
                  <Stack spacing={2} mt={0.5}>
                    {(autocompleteList.length > 0 ? autocompleteList : [{}]) // Render a default empty object if the array is empty
                      .map((item: any, index: number) => (
                        <Stack
                          key={item.id || index}
                          flexDirection="row"
                          gap={2}
                        >
                          <Autocomplete
                            disablePortal
                            size="small"
                            options={trafficSources}
                            value={item.selectedValue || null} // Ensure null when no value is selected
                            getOptionLabel={(source: any) =>
                              source.traffic_source_name
                            }
                            onChange={(_, source) => {
                              const updatedList = [...autocompleteList];
                              const updatedItem = {
                                ...item,
                                selectedValue: source || null,
                                shared_uid: source?.shared_uid || null,
                              }; // Update selectedValue and shared_uid
                              updatedList[index] = updatedItem; // Replace the item at the current index
                              setAutocompleteList(updatedList); // Update the entire list

                              // Collect all shared_uids from the updated list
                              const allSharedUids = updatedList
                                .map((entry: any) => entry.shared_uid)
                                .filter((uid) => uid); // Filter out null or undefined values
                              setTrafficSourceShareUid(allSharedUids); // Save the aggregated shared_uids
                            }}
                            sx={{ width: 418 }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Traffic Source"
                                required
                                color="secondary"
                                placeholder="Choose Traffic Source from the list"
                              />
                            )}
                          />
                          {index !== 0 && (
                            // Show "Remove" button for all other items
                            <Stack
                              alignItems="center"
                              flexDirection="row"
                              gap={1}
                            >
                              <ClearIcon
                                color="secondary"
                                onClick={() => {
                                  const updatedList = autocompleteList.filter(
                                    (_, idx) => idx !== index,
                                  ); // Remove the specific item
                                  setAutocompleteList(updatedList); // Update the list

                                  // Collect updated shared_uids after removal
                                  const allSharedUids = updatedList
                                    .map((entry: any) => entry.shared_uid)
                                    .filter((uid) => uid); // Filter out null or undefined values
                                  setTrafficSourceShareUid(allSharedUids); // Save the aggregated shared_uids
                                }}
                                sx={{ margin: "6px 16px", cursor: "pointer" }}
                              />
                            </Stack>
                          )}
                          {index === autocompleteList.length - 1 && (
                            // Add "Add Another" button to the last item only if it's not the first one
                            <Button
                              variant="outlined"
                              color="secondary"
                              onClick={handleAddTrafficSource}
                              sx={{ padding: "6px 16px", width: "192px" }}
                            >
                              <Stack
                                alignItems="center"
                                flexDirection="row"
                                gap={1}
                              >
                                Add another <AddIcon fontSize="small" />
                              </Stack>
                            </Button>
                          )}
                        </Stack>
                      ))}
                  </Stack>
                </Box>
                <Typography
                  sx={{
                    fontSize: "12px",
                    marginTop: "8px",
                    color: "#00000099",
                  }}
                >
                  Fields marked with * are mandatory.
                </Typography>
              </Box>
              <Box>
                <Stack
                  flexDirection={"row"}
                  justifyContent={"end"}
                  marginTop={2}
                  gap={1}
                >
                  <Button color="secondary" onClick={handleCancel}>
                    Cancel
                  </Button>
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={handleNext}
                    disabled={
                      groupName.length <= 7 ||
                      !selectedPopup ||
                      !selectedTheme ||
                      !popupProportion ||
                      !themeProportion ||
                      !trafficSourceShareUid ||
                      (Array.isArray(trafficSourceShareUid) &&
                        trafficSourceShareUid.length === 0) ||
                      (showSecondTheme && !selectedTheme2) ||
                      (showSecondPopup && !selectedPopup2)
                    }
                  >
                    <Stack flexDirection={"row"}>
                      Next <ChevronRightIcon />
                    </Stack>
                  </Button>
                </Stack>
              </Box>
            </>
          )}

          {/* next page of the create pop-up */}
          {currentPage === 2 && (
            <>
              <Box>
                <Grid container>
                  <Grid size={12}>
                    <Typography variant="h6">Second Headline:</Typography>
                  </Grid>

                  {/* Headline sections for Desktop, Mobile, and Tablet */}
                  {["desktop", "mobile", "tablet"].map((device) => (
                    <React.Fragment key={device}>
                      <Grid size={12} mt={1}>
                        <Typography variant="subtitle1">
                          {device.charAt(0).toUpperCase() + device.slice(1)}:
                        </Typography>
                      </Grid>
                      <Grid size={4}>
                        <Stack gap={3} flexDirection={"row"}>
                          <RadioGroup defaultValue="website">
                            <Stack
                              direction="row"
                              alignItems="center"
                              spacing={1}
                            >
                              <Radio
                                value="website"
                                sx={{
                                  color: "#9C27B0", // Purple color
                                  "&.Mui-checked": { color: "#9C27B0" },
                                }}
                                onClick={() =>
                                  handleHeadlineChange(device, "inherit")
                                }
                                checked={headlineState[device] === "inherit"}
                              />
                              <Stack>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  Inherit From
                                </Typography>
                                <Typography
                                  fontSize={16}
                                  fontWeight={400}
                                  height={20}
                                >
                                  Website
                                </Typography>
                              </Stack>
                            </Stack>
                          </RadioGroup>
                          <RadioGroup defaultValue="website">
                            <Stack
                              direction="row"
                              alignItems="center"
                              spacing={1}
                            >
                              <Stack>
                                <Typography
                                  variant="body2"
                                  color="text.secondary"
                                >
                                  Headline
                                </Typography>
                                <Typography
                                  fontSize={16}
                                  fontWeight={400}
                                  height={20}
                                >
                                  N/A
                                </Typography>
                              </Stack>
                            </Stack>
                          </RadioGroup>
                        </Stack>
                      </Grid>
                      <Grid size={8}>
                        <Stack flexDirection={"row"}>
                          <FormControlLabel
                            value={`custom-${device}`}
                            control={<Radio color="secondary" />}
                            label="Custom:"
                            onClick={() =>
                              handleHeadlineChange(device, "custom")
                            }
                            checked={headlineState[device] === "custom"}
                          />
                          <TextField
                            id={`custom-headline-${device}`}
                            label="Custom Headline"
                            placeholder={`New ${device} headline`}
                            onChange={(e) =>
                              handleTextChange(device, e.target.value)
                            }
                            fullWidth
                            color="secondary"
                            size="small"
                            disabled={headlineState[device] === "inherit"}
                          />
                        </Stack>
                      </Grid>
                    </React.Fragment>
                  ))}

                  {/* Banner Image Section */}
                  <Grid size={12} mt={2}>
                    <Typography variant="h6">Banner Image:</Typography>
                  </Grid>

                  {["desktop", "mobile", "tablet"].map((device) => {
                    const isImageUploaded =
                      device === "desktop"
                        ? bannerImagePathDesktop
                        : device === "mobile"
                          ? bannerImagePathMobile
                          : bannerImagePathTablet;

                    return (
                      <React.Fragment key={device}>
                        <Grid container alignItems="center" size={12} mt={1}>
                          <Typography variant="subtitle1">
                            {device.charAt(0).toUpperCase() + device.slice(1)}:
                          </Typography>
                        </Grid>
                        {/* Inherit option for banner images  */}
                        <Grid size={4}>
                          <Stack
                            direction="row"
                            alignItems="center"
                            gap={3}
                            mt={1}
                          >
                            <RadioGroup defaultValue="website">
                              <Stack
                                direction="row"
                                alignItems="center"
                                spacing={1}
                              >
                                <Radio
                                  value="website"
                                  sx={{
                                    color: "#9C27B0", // Purple color
                                    "&.Mui-checked": { color: "#9C27B0" },
                                  }}
                                  onClick={() =>
                                    handleBannerChange(device, "inherit")
                                  }
                                  checked={bannerState[device] === "inherit"}
                                />
                                <Stack>
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    Inherit From
                                  </Typography>
                                  <Typography
                                    fontSize={16}
                                    fontWeight={400}
                                    height={20}
                                  >
                                    Website
                                  </Typography>
                                </Stack>
                              </Stack>
                            </RadioGroup>
                            <RadioGroup defaultValue="website">
                              <Stack
                                direction="row"
                                alignItems="center"
                                spacing={1}
                              >
                                <Stack>
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    Banner
                                  </Typography>
                                  <Typography
                                    fontSize={16}
                                    fontWeight={400}
                                    height={20}
                                  >
                                    N/A
                                  </Typography>
                                </Stack>
                              </Stack>
                            </RadioGroup>
                          </Stack>
                        </Grid>
                        {/* custom banner images  */}
                        <Grid size={8}>
                          <Stack flexDirection="row" alignItems="center">
                            <FormControlLabel
                              value={`custom-${device}`}
                              control={<Radio color="secondary" />}
                              label="Custom:"
                              onClick={() =>
                                handleBannerChange(device, "custom")
                              }
                              checked={bannerState[device] === "custom"}
                            />

                            <TextField
                              id={`custom-headline-${device}`}
                              placeholder={`New ${device} Banner`}
                              onChange={(e) =>
                                handleTextChange(device, e.target.value)
                              }
                              fullWidth
                              color="secondary"
                              size="small"
                              value={isImageUploaded}
                              disabled
                              InputProps={{
                                endAdornment: (
                                  <InputAdornment position="end">
                                    {!isImageUploaded ? (
                                      <>
                                        <input
                                          aria-label="upload picture"
                                          accept="image/*"
                                          style={{ display: "none" }}
                                          id={`file-upload-${device}`}
                                          type="file"
                                          onChange={(e) => {
                                            setUploadingDevice(device);
                                            handleFileUpload(device, e);
                                          }}
                                          disabled={
                                            bannerState[device] === "inherit"
                                          }
                                        />
                                        <label
                                          htmlFor={`file-upload-${device}`}
                                        >
                                          <IconButton
                                            disabled={
                                              bannerState[device] === "inherit"
                                            }
                                            component="span"
                                          >
                                            <FileUploadOutlinedIcon
                                              color={
                                                bannerState[device] ===
                                                "inherit"
                                                  ? "disabled"
                                                  : "secondary"
                                              }
                                            />
                                          </IconButton>
                                        </label>
                                      </>
                                    ) : (
                                      <IconButton
                                        onClick={() => {
                                          if (device === "desktop")
                                            setBannerImagePathDesktop("");
                                          if (device === "mobile")
                                            setBannerImagePathMobile("");
                                          if (device === "tablet")
                                            setBannerImagePathTablet("");
                                        }}
                                      >
                                        <ClearIcon color="secondary" />
                                      </IconButton>
                                    )}
                                  </InputAdornment>
                                ),
                              }}
                            />
                          </Stack>
                        </Grid>
                      </React.Fragment>
                    );
                  })}
                </Grid>

                <Stack
                  flexDirection={"row"}
                  justifyContent={"end"}
                  marginTop={2}
                  gap={1}
                >
                  <Button color="secondary" onClick={handleCancel}>
                    Cancel
                  </Button>
                  <Button
                    color="secondary"
                    onClick={handleBack}
                    variant="outlined"
                  >
                    <Stack flexDirection={"row"} gap={1}>
                      <KeyboardArrowLeftIcon /> BACK
                    </Stack>
                  </Button>
                  <Button
                    data-tracker="38"
                    color="secondary"
                    variant="contained"
                    onClick={() => setPageGroupCreationData(true)}
                    disabled={creationLoading}
                  >
                    <Stack flexDirection={"row"} gap={1} alignItems="center">
                      {creationLoading ? (
                        <>
                          Create <CircularProgress size={20} color="inherit" />
                        </>
                      ) : (
                        <>
                          Create <AutoFixHighIcon />
                        </>
                      )}
                    </Stack>
                  </Button>
                </Stack>
              </Box>
            </>
          )}
        </Paper>
      </Dialog>
    </>
  );
}

export default CreateNewRequestModal;
